(ns common.schema.entities.workspace.kyc.templates.lifeline.legal-entity
  (:require
   [clojure.string :as string]
   [common.schema.entities.workspace.kyc.questions.questions-registry :as questions-registry]
   [common.schema.entities.workspace.kyc.templates.flow-through-jurisdiction :as flow-through-jurisdiction]
   [common.schema.entities.workspace.kyc.templates.lifeline.controlling-person :as controlling-person]
   [common.schema.entities.workspace.kyc.templates.lifeline.ubo :as ubo]
   [common.schema.entities.workspace.kyc.templates.lifeline.pep :as pep]
   [common.schema.entities.workspace.kyc.templates.lifeline.smo :as smo]
   [common.schema.entities.workspace.kyc.templates.lifeline.substantial-us-owner :as substantial-us-owner]
   [common.schema.entities.workspace.kyc.templates.lifeline.company-config :as lifeline-company-config]
   [common.util :as cu]))

(def mandatory-field-meta
  {:mandatory-field? true})

(defn beneficial-ownership-as-trustee?
  [answers]
  (= :as-trustee (:kyc-legal-entity/beneficial-ownership answers)))

(defn beneficial-ownership-as-itself?
  [answers]
  (= :as-itself (:kyc-legal-entity/beneficial-ownership answers)))

(def substantial-us-owners-info-en
  (str "<p><strong>Substantial U.S. Owner</strong> (as defined in Regulations section 1.1473-1(b)) means generally:</p>"
       "<ul>"
       "<li>With respect to any foreign corporation, any Specified U.S. Person that owns, directly or indirectly, more than 10 percent of the stock of such corporation (by vote or value);</li>"
       "<li>With respect to any foreign partnership, any Specified U.S. Person that owns, directly or indirectly, more than 10 percent of the profits interests or capital interests in such partnership; and</li>"
       "<li>In the case of a trust:"
       "<ul>"
       "<li>Any Specified U.S. Person treated as an owner of any portion of the trust under sections 671 through 679 of the IRC; and</li>"
       "<li>Any Specified U.S. Person that holds, directly or indirectly, more than 10 percent of the beneficial interests of the trust.</li>"
       "</ul>"
       "</li>"
       "</ul>"))

(def controlling-persons-info-en
  (str  "<p><strong>Controlling Person</strong> means the natural persons who exercise direct or indirect control over an entity. In the case of a trust, this means the settlor(s), trustee(s), protector(s) (if any), beneficiary(ies) or class(es) of beneficiaries, and any other natural person(s) exercising ultimate effective control over the trust. In the case of a legal arrangement other than a trust, this means persons in equivalent or similar positions. The term 'Controlling Persons' shall be interpreted in a manner consistent with the Financial Action Task Force (FATF) Recommendations.</p>"

        "<p><strong>FATF guidance on identifying Controlling Persons</strong> (beneficial owners):</p>"
        "<ul>"
        "<li>Identify the natural persons (if any) who ultimately have a controlling ownership interest in a legal person; and</li>"
        "<li>To the extent there is doubt as to whether the person(s) with the controlling ownership interest are the beneficial owner(s), or where no natural person exerts control through ownership interests, identify the natural persons (if any) exercising control through other means; and</li>"
        "<li>Where no natural person is identified under the two points above, identify and take reasonable measures to verify the identity of the relevant natural person who holds the position of senior managing official.</li>"
        "</ul>"))

(defn fatca-non-us-passive-nfe?
  [answers _kyc-process]
  (and (-> answers
           :kyc-legal-entity/us-person-status
           (= :not-us-person))
       (-> answers
           :kyc-legal-entity/lifeline-fatca-is-financial-institution
           (= :no))
       (-> answers
           :kyc-legal-entity/lifeline-fatca-nffe-fatca-status
           (= :passive-nffe))))

(defn crs-passive-nfe?
  [answers _kyc-process]
  (and (-> answers
           :kyc-legal-entity/lifeline-crs-is-financial-institution
           (= :no))
       (-> answers
           :kyc-legal-entity/lifeline-crs-nfe-classification
           (= :passive-nfe))))

(defn crs-fi-investment-entity-in-non-partipating-jurisdiction?
  [answers _kyc-process]
  (and (-> answers
           :kyc-legal-entity/lifeline-crs-is-financial-institution
           (= :yes))
       (-> answers
           :kyc-legal-entity/lifeline-crs-fi-classification
           (= :investment-entity-resident-in-a-non-participating-jurisdiction-under-crs-and-managed-by-other-fi))))

(defn substantial-us-owners-mandatory?
  [answers kyc-process]
  (and (fatca-non-us-passive-nfe? answers kyc-process)
       (not= (:kyc-process/status kyc-process)
             :kyc-process.status/draft)
       (-> answers
           :kyc-legal-entity/controlling-persons-edn
           empty?)))

(defn controlling-persons-mandatory?
  [answers kyc-process]
  (or (and (fatca-non-us-passive-nfe? answers kyc-process)
           (not= (:kyc-process/status kyc-process)
                 :kyc-process.status/draft)
           (-> answers
               :kyc-legal-entity/substantial-us-owners-edn
               empty?))
      (and (or (crs-passive-nfe? answers kyc-process)
               (crs-fi-investment-entity-in-non-partipating-jurisdiction? answers kyc-process))
           (not= (:kyc-process/status kyc-process)
                 :kyc-process.status/draft))))

(def schema
  {:kyc-template/template-name
   "Legal entity"

   :kyc-template/kyc-form-preface
   {:fi ""
    :en (str "<p>As a Fund Manager, Lifeline Ventures has a legal obligation to know and identify its investors. Lifeline Ventures must always comply with applicable anti-money laundering and tax reporting regulations. Anti-money laundering regulations require Lifeline Ventures to collect information e.g. on each Investor’s financial status, source of funds and ultimate beneficial owners before establishing a client relationship or conducting a transaction. In case the investor refuses to provide Lifeline Ventures with any obligatory information, Lifeline Ventures may not be able to enter into a business relationship or to operate in a normal course of business with the investor. Please note that additional information relating to the investor’s type of business entity, jurisdiction, and/or information provided in this form may be requested.</p>"

             "<p>Customer identification information and other personal data may be used to prevent, disclose, and investigate money laundering and terrorist financing, as well as to prosecute acts of money laundering and terrorist financing, and to investigate the crime through which the assets or proceeds derived from money laundering or terrorist financing were obtained.</p>"

             "<p>Tax reporting regulations require Lifeline Ventures to collect information such as an investor’s tax residence and tax identification numbers. In accordance with international tax regulation (FATCA, CRS and DAC2) Lifeline Ventures is obligated to know in which country/countries the investor and its Ultimate Beneficial Owners have tax residency.</p>"

             "<p>In accordance with the applicable Luxembourg data protection law, as amended and, in accordance with the Regulation n°2016/679 of 27 April 2016 on the protection of natural persons with regard to the processing of personal data and on the free movement of such data (<strong>“GDPR”</strong>), <strong>fundcraft Management S.à r.l.</strong> (the <strong>“AIFM”</strong>) and fundcraft Services S.à r.l. (the <strong>“PSF”</strong>) act mainly as data processor and <strong>fundcraft Holdco S.à r.l.</strong> (the <strong>“Holdco”</strong>) might act as a data sub-processor, and may collect, store and process by electronic or other means, personal data as defined in GDPR (<strong>“Personal Data”</strong>). The data is collected via <strong>Zapflow Oy</strong> (<strong>“Zapflow”</strong>) (acting as data processor) technical portal. <sttrong>LLV Fund Management Oy</strong> (<i>Lifeline Ventures</i>, the Fund Manager) acts as the data controller.</p>"

             "<p>In accordance with GDPR, Personal Data will not be retained for a period longer than necessary for the purpose of the data processing, subject to applicable statutory periods of retention (such as required by applicable Anti-Money Laundering (AML) regulations and Know Your Customer (KYC) obligations; i.e. Law of 12 November 2004 on the fight against money laundering and terrorist financing, as amended, CSSF Regulation 12/02, as amended, and other relevant regulations and their amendments, from time to time).</p>"

             "<p>The Personal Data may also be transferred by the AIFM, the PSF, the Holdco, Zapflow, and/or by The Fund Manager to further service providers, in the context of the above-mentioned purposes, refers to the depositary, the paying agent, the portfolio manager, the investment advisor, the distributors, the auditor and/or legal adviser(s).</p>"

             "<p>Please consider you may exercise rights;</p>"
             "<ul>"
             "<li>to access your personal data;</li>"
             "<li>to correct your personal data where it is inaccurate or incomplete;</li>"
             "<li>to object to the processing of your personal data;</li>"
             "<li>to ask for erasure of your Personal Data;</li>"
             "<li>to ask for personal data portability.</li>"
             "</ul>"
             "<p></p>"
             "<p>By writing to the AIFM or the PSF at the following address :</p>"
             "<p>12, Rue des Mérovingiens, L-8070 Bertrange, Grand Duchy of Luxembourg</p>"
             "<p>Or an e-mail to : <a href=\"mailto:dataprotection@fundcraft.lu\">dataprotection@fundcraft.lu</a></p>"

             "<p>The Fund Manager contact information is :</p>"
             "<p>LLV Fund Management Oy, Pursimiehenkatu 26 C, 00150 Helsinki</p>"
             "<p>Email: <a href=\"mailto:compliance@lifelineventures.com\">compliance@lifelineventures.com</a></p>"

             "<p>Full privacy policies are available at <a style=\"padding:0px\" href=\"https://www.fundcraft.lu/wp-content/uploads/2024/07/Privacy-Notice_July2024.pdf\" target=\"_blank\">https://www.fundcraft.lu/wp-content/uploads/2024/07/Privacy-Notice_July2024.pdf</a> and <a style=\"padding:0px\" href=\"https://www.lifelineventures.com/app/uploads/2024/03/privacy-statement_lifeline-ventures-2.pdf\" target=\"_blank\">https://www.lifelineventures.com/app/uploads/2024/03/privacy-statement_lifeline-ventures-2.pdf</a>.</p>")}

   :kyc-template/svg-logo               ""                                                          ;; TODO
   :kyc-template/png-logo               "/img/customer/lifeline/lifeline_logo_black.png"
   :kyc-template/png-logo-print-width   70
   :kyc-template/png-logo-print-margins [0 0 0 20]

   :kyc-template/help-email-address lifeline-company-config/contact-email

   :kyc-template/available-languages    [:ENG]

   :kyc-template/kyc-form-title
   {:translations {:en "Know Your Customer (KYC) form"
                   :fi "Asiakastietokysely"}}

   :kyc.legal-entity/basic-information
   {:ord
    1.0
    :translations
    {:en "Basic information"
     :fi "Perustiedot"}
    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/name mandatory-field-meta]
      [:kyc-legal-entity/incorporation-date mandatory-field-meta]
      [:kyc-legal-entity/country-of-incorporation mandatory-field-meta]
      [:kyc-legal-entity/business-id mandatory-field-meta]

      [:kyc-legal-entity/legal-form mandatory-field-meta]
      [:kyc-legal-entity/legal-form-other
       {:mandatory-field? (fn [answers]
                            (= :legal-form/other (:kyc-legal-entity/legal-form answers)))}]

      [:kyc-legal-entity/actual-line-of-business mandatory-field-meta]
      [:kyc-legal-entity/actual-line-of-business-other
       {:mandatory-field? (fn [answers]
                            (some #{:industry/other}
                                  (:kyc-legal-entity/actual-line-of-business answers)))}]

      [:kyc-legal-entity/primary-contact-email mandatory-field-meta]
      [:kyc-legal-entity/nature-and-purpose-of-business-relationship mandatory-field-meta]
      [:kyc-legal-entity/nature-and-purpose-of-business-relationship-other]


      ;; Third party investment questions (LE.10.1-10.3)
      [:kyc-legal-entity/is-the-investment-made-on-your-own-behalf mandatory-field-meta]
      [:kyc-legal-entity/name-of-third-party
       {:mandatory-field? (fn [answers]
                            (= :no (:kyc-legal-entity/is-the-investment-made-on-your-own-behalf answers)))}]
      [:kyc-legal-entity/country-of-third-party
       {:mandatory-field? (fn [answers]
                            (= :no (:kyc-legal-entity/is-the-investment-made-on-your-own-behalf answers)))}]])}

   :kyc.legal-entity/address-information
   {:ord
    2.0
    :translations
    {:en "Address"
     :fi "Osoite"}
    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/address mandatory-field-meta]
      [:kyc-legal-entity/state {:optional-field? true}]
      [:kyc-legal-entity/zip-code mandatory-field-meta]
      [:kyc-legal-entity/city mandatory-field-meta]
      [:kyc-legal-entity/country mandatory-field-meta]
      [:kyc-legal-entity/main-business-address
       {:translations {:en "Main business address"
                       :fi "Pääasiallisen liiketoimintapaikan osoite (jos eroaa kotipaikan osoitteesta)"}}]

      ;; Optional mailing address fields (if different from main address)
      [:kyc-legal-entity/mailing-address {:optional-field? true}]
      [:kyc-legal-entity/mailing-state {:optional-field? true}]
      [:kyc-legal-entity/mailing-zip-code {:optional-field? true}]
      [:kyc-legal-entity/mailing-city {:optional-field? true}]
      [:kyc-legal-entity/mailing-country {:optional-field? true}]])}

   :kyc.legal-entity/ownership-structure
   {:ord
    5.5
    :translations
    {:en "Ownership structure determination"
     :fi "Omistusrakenteen määrittäminen"}
    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/natural-person-25-percent-ownership mandatory-field-meta]])}

   :kyc-legal-entity/smo-data
   {:ord
    6.0
    :translations
    {:en "Senior Managing Officers (SMO)"
     :fi "Johtavat toimihenkilöt"}
    :conditional-block (fn [answers _kyc-process]
                         (= :no (:kyc-legal-entity/natural-person-25-percent-ownership answers)))
    :info
    {:en (str "<p>When no natural person holds 25% or more ownership, "
              "please provide information about Senior Managing Officers who have "
              "executive authority over the entity.</p>"
              "<p>This typically includes the CEO, Managing Director, and other key "
              "executives with decision-making authority.</p>")
     :fi (str "<p>Kun mikään luonnollinen henkilö ei omista 25% tai enempää, "
              "anna tiedot johtavista toimihenkilöistä, joilla on "
              "toimeenpanovalta yhteisössä.</p>"
              "<p>Tähän kuuluvat tyypillisesti toimitusjohtaja, toimitusjohtaja ja muut "
              "avainhenkilöt, joilla on päätöksentekovalta.</p>")}
    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/smo-edn
       {:data/template    smo/schema
        :mandatory-field? (fn [answers]
                            (= :no (:kyc-legal-entity/natural-person-25-percent-ownership answers)))}]])}

   :kyc-legal-entity/ubo-data
   {:ord               6.1
    :translations
    {:en "Ultimate beneficial owners (UBO)"
     :fi "Yhteisön tosiasialliset edunsaajat"}

    :conditional-block (fn [answers _kyc-process]
                         (= :yes (:kyc-legal-entity/natural-person-25-percent-ownership answers)))

    :info
    {:fi
     (str
      "<p>Tosiasialliset edunsaajat, jotka käyttävät määräysvaltaa yhtiössä.</p>"
      "<p><strong>Huomaathan, että jokaisessa yhtiössä on tosiasiallinen edunsaaja.</strong></p>"
      "<p>Luonnollisen henkilön katsotaan käyttävän määräysvaltaa yhteisössä, jos hänellä suoraan tai epäsuorasti on:"
      "<ol><li><strong>25% tai suurempi omistusosuus yhteisön osakkeista tai äänivallasta perustuen nomistukseen, "
      "jäsenyyteen, yhtiöjärjestykseen/sääntöihin, yhtiösopimukseen tai muuhun sopimukseen tai sääntöihin.</strong></li>"
      "<li><strong>oikeus nimittää tai erottaa enemmistö yhteisön hallituksen tai muun vastaavan toimielimen jäsenistä "
      "ja, että tällainen oikeus perustuu kohdassa 1. määriteltyihin perusteisiin.</strong></li></ol></p>"
      "<p>Mikäli yhteisön oikeudellinen muoto on yhdistys, uskonnollinen yhteisö tai muu yhteisö, jolla ei ole "
      "edellä määriteltyjä tosiasiallisia edunsaajia, katsotaan hallituksen jäsenten, "
      "(jotka ovat merkittynä yhdistysrekisteriin tai vastaavaan) olevan tosiasiallisia edunsaajia.</p>"
      "<p>Mikäli yhteisö on säätiö (säätiölaki 487/2015), katsotaan hallituksen sekä hallintoneuvoston jäsenten, "
      "(jotka ovat merkittynä säätiörekisteriin tai vastaavaan) olevan tosiasiallisia edunsaajia.</p>"
      "<p><strong>Mikäli yhteisössä ei ole kohdissa 1. ja 2. määriteltyjä henkilöitä, katsotaan toimitusjohtajan ja/tai "
      "hallituksen jäsenten olevan yhteisön tosiasiallisia edunsaajia ellei yhteisö toisin ilmoita.</strong></p>")
     :en
     (str
      "<p>Ultimate Beneficial Owners ('UBOs') who exercise control over an entity.</p>"
      "<p>Please note that every entity has an UBO. A natural person is considered to have control over an entity, "
      "when he/she either directly or indirectly has:"
      "<ol><li><strong>25% or more of shares or voting rights based on ownership, membership, articles of "
      "association, partnership agreement or corresponding rules or other agreement;</strong></li>"
      "<li><strong>The right to nominate or dismiss the majority of the board members or members of another "
      "corresponding organ of the entity, and such right is based on the same grounds as under subsection (1) "
      "above.</strong></li></ol></p>"
      "<p>In case the entity is an association (an association defined in Associations Act 503/1989) or a "
      "religious community or a similar entity which does not have UBOs defined above, the members of the "
      "Board of Directors (members that are registered in the association register or similar) are considered "
      "UBOs.</p>"
      "<p>If the entity is a foundation (Foundations Act, 487/2015), the members of the Board of Directors and "
      "the members of the Supervisory Board (members that are registered in the foundation register or similar) "
      "are considered UBOs.</p>"
      "<p>If the entity is a foreign trust: settlors, trustees, protectors, beneficiaries, classes of "
      "beneficiaries and any other natural persons exercising ultimate effective control over the trust "
      "are considered UBOs.</p>"
      "<p><strong>If an entity does not have persons referred to in subsections (1) or (2) above, the CEO and the "
      "members of the Board of Directors are considered UBOs, unless the entity notifies otherwise.<strong></p>")}
    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/ubo-edn
       {:data/template    ubo/schema
        :conditional-field (fn [answers _kyc-process]
                             (= :yes (:kyc-legal-entity/natural-person-25-percent-ownership answers)))
        :mandatory-field? (fn [answers _kyc-process]
                            (= :yes (:kyc-legal-entity/natural-person-25-percent-ownership answers)))}]])}

   :kyc-legal-entity/PEP-data
   {:ord
    7.0
    :translations
    {:en "Politically exposed person (PEP)"
     :fi "Poliittinen vaikutusvalta (PEP)"}
    :info
    {:en
     (str
      "<p>This list automatically contains all UBO's who are marked as PEP. If the investor has other "
      "politically exposed persons, add them here.</p>"
      "<p>Confirm for instance following positions:"
      "<ul><li>Member of the board</li>"
      "<li>Chief executive officer</li>"
      "<li>Representative (for example the signatories of this form)</li></ul></p>"
      "<p>A Politically Exposed Person is defined in the 2004 Law as a person who is:</p>"

      "<p>(1) An individual who is or has been entrusted with a prominent public function or by an international organisation, including:"

      "<ul>"
      (->> ["heads of State, heads of government, ministers and deputy or assistant ministers;"
            "members of parliament “or of similar legislative bodies”; "
            "members of supreme courts, of constitutional courts or of other high-level judicial bodies, the decisions of which are not subject to further appeal, except in exceptional circumstances;"
            "members of courts of auditors or of the boards “or directorates” of central banks;"
            "ambassadors, chargés d’affaires and high-ranking officers in the armed forces;"
            "members of the administrative, management or supervisory bodies of State-owned enterprises;"
            "important officials “and members of the governing bodies” of political parties;”"
            "directors, deputy directors and members of the board or equivalent function of an international organisation"
            "the natural persons exercising the functions included in the list published by the European Commission related to “all prominent public functions”"]
           (mapv #(str "<li>" % "</li>"))
           (string/join ""))
      "</ul>"

      "<p></p>"

      "<p>None of the categories set out above shall be understood as covering middle ranking or more junior officials.</p>"

      "<p>(2) An immediate family member of a person mentioned in sub-paragraph (a), including any of the following:"

      "<ul>"
      (->> ["the spouse;"
            "any partner considered by national law as equivalent to the spouse;"
            "the children and their spouses or partners considered by national law as equivalent to a spouse;"
            "the parents;"
            "the brothers and sisters."]
           (mapv #(str "<li>" % "</li>"))
           (string/join ""))
      "</ul>"

      "<p></p>"

      "<p>(3) Persons known to be close associates shall mean all natural persons, including:</p>"

      "<ul>"

      (->> ["any natural person who is known to have joint beneficial ownership of legal entities or legal arrangements, or any other close business relations, with a person referred to in paragraph;"
            "any natural person who has sole beneficial ownership of a legal entity or legal arrangement which is known to have been set up for the benefit de facto of the person referred to in (1)."]
           (mapv #(str "<li>" % "</li>"))
           (string/join ""))

      "</ul>")

     :fi
     (str
      "<p>Lisää tähän poliittisesti vaikutusvaltaiset henkilöt joita et ole jo kirjannut edelliselle "
      "UBO listalle.</p>"
      "<p>Muista esimerkiksi henkilöt seuraavissa positioissa:"
      "<ul><li>Hallituksen jäsen</li>"
      "<li>Toimitusjohtaja</li>"
      "<li>Henkilöt, joilla on nimenkirjoitusoikeus (esimerkiksi tämän lomakkeen allekirjoittaja)</li></ul></p>"
      "<p>Henkilö on poliittisesti vaikutusvaltainen, jos hän tai hänen lähipiiriinsä kuuluva henkilö "
      "toimii tai on toiminut merkittävässä julkisessa virassa Suomessa tai toisessa valtiossa.</p>")}
    #_:more-info
    #_{:link-text   {:en "More information"
                     :fi "Lisätietoa täällä"}
       :info-header {:en "Politically exposed person (PEP)"
                     :fi "Poliittinen vaikutusvalta (PEP)"}
       :info-content
       {:en
        (str
         "<p>Politically Exposed Person <strong>(“PEP”)</strong> refers to a person who is or "
         "during <strong>the last 12 months</strong> "
         "has been in a prominent public function, a close associate or an immediate family member "
         "of such a person either in Finland or another country:</p>"
         "<p><strong>A prominent public function is acting as:</strong>"
         "<ul><li>Head of State, Head of Government, minister, deputy or assistant minister</li>"
         "<li>Member of Parliament</li>"
         "<li>member of supreme court, member of constitutional court or member of some "
         "corresponding judicial body whose decisions are not generally subject to further appeal</li>"
         "<li>member of court of auditors or of the highest decision-making body of an "
         "authority auditing the management of Government funds and corresponding to the "
         "State Audit Office</li>"
         "<li>member of the Board of a central bank</li>"
         "<li>ambassador or chargé d’affaires</li>"
         "<li>high-ranking officer (minimum rank of general) in the armed forces</li>"
         "<li>member of the administrative, management or supervisory body of a "
         "state-owned company</li>"
         "<li>Member of the EU Parliament</li>"
         "<li>commissioner</li>"
         "<li>Member of the EU Court of Justice</li>"
         "<li>Member of the EU Court of Auditors</li></ul></p>"
         "<p><strong>The associates of politically exposed persons are:</strong>"
         "<ul><li>Any natural person who is known to have joint beneficial ownership of a "
         "corporation, entrepreneur or legal arrangement, or any other close business relations, "
         "with a politically exposed person</li>"
         "<li>Any natural person who has sole beneficial ownership of a corporation, "
         "entrepreneur or legal arrangement which is known to have been set up for the benefit "
         "of a politically exposed person</li></ul></p>"
         "<p><strong>The immediate family members of politically exposed persons are:</strong>"
         "<ul><li>The spouse or any partner considered equivalent to a spouse by national law;</li>"
         "<li>The children and their spouses or partners referred to above; and</li>"
         "<li>The parents.</li></ul></p>")
        :fi
        (str
         "Poliittisesti vaikutusvaltainen henkilö <strong>(“PEP”)</strong> toimii tai on toiminut "
         "merkittävässä julkisessa virassa, kuuluu tai on kuulunut sellaisen henkilön lähipiiriin "
         "tai on tai on ollut sellaisen henkilön perheenjäsen tai lähipiiriin kuuluva, Suomessa "
         "tai toisessa valtiossa <strong>viimeisen 12 kuukauden aikana</strong>:</p>"
         "<p><strong>Merkittävänä poliittisena virkana pidetään seuraavia virkoja:</strong>"
         "<ul><li>Valtion- tai hallituksen päämies, ministeri, vara-tai apulaisministeri</li>"
         "<li>Parlamentin jäsen</li>"
         "<li>Korkeimman oikeuden, perustuslakituomioistuimen tai muun vastaavan oikeuselimen jäsen</li>"
         "<li>Keskuspankin johtokunnan jäsen</li>"
         "<li>Suurlähettiläs</li>"
         "<li>Puolustusvoimien kenraalikuntaan kuuluva upseeri</li>"
         "<li>Valtionyritysten hallinto-, johto- ja valvontaelimen jäsen</li>"
         "<li>EU-parlamentin jäsen</li>"
         "<li>Komission jäsen</li>"
         "<li>EU-tuomioistuimen jäsen</li>"
         "<li>EU:n tilintarkastustuomioistuimen jäsen</li></ul></p>"
         "<p><strong>Poliittisesti vaikutusvaltaisen henkilön lähipiiriin kuuluvaksi katsotaan:</strong>"
         "<ul><li>Luonnollinen henkilö, joka toimii poliittisesti vaikutusvaltaisen henkilön "
         "yhtiökumppanina</li>"
         "<li>Luonnollinen henkilö, jonka omistaman yhtiön tosiasiallisena edunsaajana on "
         "poliittisesti vaikutusvaltainen henkilö.</li></ul></p>"
         "<p><strong>Poliittisesti vaikutusvaltaisen henkilön lähipiiriin kuuluvaksi katsotaan:</strong>"
         "<ul><li>Poliittisesti vaikutusvaltaisen henkilön puoliso tai puolisoon kansallisen "
         "lain mukaisesti rinnastettava kumppani</li>"
         "<li>Poliittisesti vaikutusvaltaisen henkilön lapset sekä heidän puolisonsa tai puolisoon "
         "kansallisen lain mukaisesti rinnastettava kumppani.</li>"
         "<li>Poliittisesti vaikutusvaltaisen henkilön vanhemmat</li></ul></p>")}}



    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/has-peps? {:mandatory-field? true}]
      [:kyc-legal-entity/pep-edn {:data/template pep/schema
                                  :mandatory-field?
                                  (fn [answers]
                                    (= :yes (:kyc-legal-entity/has-peps? answers)))}]])}

   :kyc.legal-entity/corporation-structure-and-incorporation-documents
   {:ord          8.0
    :translations {:en "Corporate Structure & Incorporation Documents"}
    :questions
    (questions-registry/resolve-questions
     (concat
      [[:kyc-legal-entity/articles-of-association-or-incorporation mandatory-field-meta]

       [:kyc-legal-entity/trade-register-extract-of-entity-mandatory
        {:info {:en "<p>Dated within the last 6 months</p>"}}]

       [:kyc-legal-entity/attachment-organization-chart
        {:conditional-field (constantly true)
         :mandatory-field?  (constantly true)
         :info              {:en (str "<p>Show the full chain of ownership to the ultimate owners. "
                                      "List every intermediary entity/shareholder and the percentage of ownership or control at each step.</p>")}}]

       [:kyc-legal-entity/signed-share-register
        {:info             {:en "<p>Dated within the last 6 months</p>"}
         :mandatory-field? true}]

       [:kyc-legal-entity/identification-for-all-legal-representatives mandatory-field-meta]

       [:kyc-legal-entity/contact-matrix-file
        {:optional-field? true}]]))}

   :kyc.legal-entity/financial-information
   {:ord          9.0
    :translations {:en "Financial information"
                   :fi "Pääoma"}
    :questions
    (questions-registry/resolve-questions
     (concat
      [[:kyc-legal-entity/latest-available-financial-statement mandatory-field-meta]
       [:kyc-legal-entity/recent-bank-statement mandatory-field-meta]]))}

   :kyc.legal-entity/compliance-and-risk-management
   {:ord          9.2
    :translations {:en "Compliance & Risk Management"}
    :questions
    (questions-registry/resolve-questions
     (concat
      [[:kyc-legal-entity/aml-letter]
       ;; Crime proceeds confirmation (D.2.1)
       [:kyc-legal-entity/confirm-funds-not-proceeds-of-crime
        {:conditional-field (fn [answers]
                              (not (cu/blank-value? (:kyc-legal-entity/aml-letter answers))))
         :mandatory-field? (fn [answers]
                             (not (cu/blank-value? (:kyc-legal-entity/aml-letter answers))))}]]))}

   :kyc.legal-entity/beps-questionnaire
   {:ord          10.0
    :translations {:en "BEPS Questionnaire"}
    :info         {:en (str "<p>This BEPS Questionnaire requests certain information that is required by the General Partner to "
                            "ease the identification of hybrid mismatches and corresponding tax implications arising in relation to "
                            "ATAD 2 as regards hybrid mismatches with third countries (and equivalent legislation in other jurisdictions).</p>"
                            "<br/><br/>"
                            "<p>This is a preliminary information request. The Investor acknowledges that (i) the answers given in to the questions in this BEPS Questionnaire will be relevant, "
                            "amongst other matters, to determination of the tax position of the Partnership, any alternative investment vehicle or any "
                            "portfolio entity (including with respect to tax laws implementing ATAD 2) and (ii) the General Partner reserves the right to "
                            "request further information to complete the analysis required to assess any tax implications.</p>")}
    :questions
    (questions-registry/resolve-questions
     (concat
      [[:kyc-legal-entity/is-the-investor-tax-resident-in-a-jurisdiction]]

      (let [condition-fn (fn [answers]
                           (= :yes (:kyc-legal-entity/is-the-investor-tax-resident-in-a-jurisdiction answers)))
            config {:conditional-field condition-fn
                    :mandatory-field?  condition-fn}]
        [[:kyc-legal-entity/jurisdiction-of-tax-residence config]
         [:kyc-legal-entity/tin-in-the-jurisdiction-of-tax-residence config]])

      [[:kyc-legal-entity/tax-status-in-home-jurisdiction mandatory-field-meta]
       [:kyc-legal-entity/beneficial-ownership mandatory-field-meta]]

      (let [config {:conditional-field beneficial-ownership-as-itself?
                    :mandatory-field?  beneficial-ownership-as-itself?}]
        [[:kyc-legal-entity/scsp-transparent-under-investors-laws config]
         [:kyc-legal-entity/investor-taxed-on-partnership-income config]
         [:kyc-legal-entity/investor-jurisdiction-lacks-tax-rules-on-scsp config]])

      (let [condition-fn
            (fn [answers]
              (and (beneficial-ownership-as-itself? answers)
                   (or (= :false (:kyc-legal-entity/scsp-transparent-under-investors-laws answers))
                       (= :false (:kyc-legal-entity/investor-taxed-on-partnership-income answers))
                       (= :true (:kyc-legal-entity/investor-jurisdiction-lacks-tax-rules-on-scsp answers)))))]
        [[:kyc-legal-entity/investor-transparent-territorial-tax-exempt-tax-relief-multichoice
          {:conditional-field condition-fn
           :mandatory-field?  condition-fn}]])

      (let [conf {:conditional-field beneficial-ownership-as-trustee?
                  :mandatory-field?  beneficial-ownership-as-trustee?}]
        [[:kyc-legal-entity/ibo-name conf]
         [:kyc-legal-entity/is-the-ibo-resident-in-a-jurisdiction-for-tax-purposes conf]])

      (let [condition-fn (fn [answers]
                           (and (beneficial-ownership-as-trustee? answers)
                                (= :yes (:kyc-legal-entity/is-the-ibo-resident-in-a-jurisdiction-for-tax-purposes answers))))
            conf {:conditional-field condition-fn
                  :mandatory-field?  condition-fn}]
        [[:kyc-legal-entity/ibo-jurisdiction-of-tax-residence conf]])

      (let [conf {:conditional-field beneficial-ownership-as-trustee?
                  :mandatory-field?  beneficial-ownership-as-trustee?}]
        [[:kyc-legal-entity/please-confirm-ibo-statements-info conf]
         [:kyc-legal-entity/scsps-transparent-under-ibos-laws conf]
         [:kyc-legal-entity/ibo-taxed-on-partnership-income conf]
         [:kyc-legal-entity/ibo-jurisdiction-lacks-tax-rules-on-scsp conf]])

      (let [condition-fn (fn [answers]
                           (and (beneficial-ownership-as-trustee? answers)
                                (or (= :false (:kyc-legal-entity/scsps-transparent-under-ibos-laws answers))
                                    (= :false (:kyc-legal-entity/ibo-taxed-on-partnership-income answers))
                                    (= :true (:kyc-legal-entity/ibo-jurisdiction-lacks-tax-rules-on-scsp answers)))))]
        [[:kyc-legal-entity/ibo-transparent-territorial-tax-exempt-tax-relief-multichoice
          {:conditional-field condition-fn
           :mandatory-field?  condition-fn}]])

      [[:kyc-legal-entity/depending-on-ibo-answers-above-we-may-require-further-information-from-ibo
        {:conditional-field beneficial-ownership-as-trustee?}]]))}

   :kyc.legal-entity/flow-through-ownership-jurisdictions
   (let [block-condition (fn [answers]
                           (or (= :tax-status/flow-through (:kyc-legal-entity/tax-status-in-home-jurisdiction answers))
                               (-> answers
                                   :kyc-legal-entity/investor-transparent-territorial-tax-exempt-tax-relief-multichoice
                                   set
                                   (some [:transparent]))
                               (-> answers
                                   :kyc-legal-entity/ibo-transparent-territorial-tax-exempt-tax-relief-multichoice
                                   set
                                   (some [:transparent]))))]
     {:ord 10.005
      :translations {:en "BEPS Questionnaire - Flow-through ownership jurisdictions"}
      :full-width-section? true
      :conditional-block block-condition
      :questions
      (questions-registry/resolve-questions
       (let [conf {:conditional-field block-condition
                   :mandatory-field?  block-condition
                   :data/template     flow-through-jurisdiction/schema}]
         [[:kyc-legal-entity/flow-through-ownership-jurisdictions conf]
          [:kyc-legal-entity/lifeline-gp-or-aifm-maybe-require-more-information-info conf]]))})

   :kyc.legal-entity/fatca-srs-self-certification-info
   {:ord                10.01
    :translations       {:en "FATCA/CRS Self-Certification"}
    :questions          []                                                                          ;; Must define as empty array so section is visible
    :info-only-section? true
    :info               {:en (str "<p><strong>Instructions for completion</strong></p><br/>"

                                  "<p>We are obliged under local laws and regulations to collect and report to the Luxembourg tax "
                                  "authorities certain information about financial accounts held by some of our Account "
                                  "Holders. Please complete the sections below as directed and provide any additional "
                                  "information that is requested. Please note that we may be obliged to share this information "
                                  "with relevant tax authorities. Terms referenced in this form and not defined in info sections of "
                                  "\"U.S. Persons\", \"US FATCA Classification for all Non-United States Entities\", "
                                  "\"Controlling persons & Substantial U.S. Owners\" or \"CRS Declaration\" -sections"
                                  " hereto shall have the same meaning as applicable under the law of 24 July 2015 "
                                  "implementing FATCA as amended or modified from time to time and any successor law "
                                  "thereto (“Luxembourg FATCA Law”) and the law of December 18, 2015 implementing "
                                  "Council Directive 2014/107/EU amending Directive 2011/16/EU as regards mandatory "
                                  "automatic exchange of information in the field of taxation and the OECD Common Reporting "
                                  "Standard as amended or modified from time to time and any successor law thereto "
                                  "(“Luxembourg CRS Law”), as relevant.</p>"
                                  "<br/><br/>"
                                  "<p>If any of the information below regarding your tax residence or automatic exchange of "
                                  "information (“AEOI”) classification changes in the future, please ensure you advise us of "
                                  "these changes promptly. If you have any questions about how to complete this form, please "
                                  "refer to accompanying guidelines for completion or contact your tax advisor.</p>")}}

   :kyc.legal-entity/us-persons
   {:ord          10.1
    :translations {:en "U.S. Persons"}
    :questions
    (questions-registry/resolve-questions
     (concat
      [[:kyc-legal-entity/us-person-status]
       [:kyc-legal-entity/us-tin
        {:conditional-field (fn [answers]
                              (-> answers
                                  :kyc-legal-entity/us-person-status
                                  (= :specified-us-person)))
         :mandatory-field?  (fn [answers]
                              (-> answers
                                  :kyc-legal-entity/us-person-status
                                  (= :specified-us-person)))}]]

      [[:kyc-legal-entity/us-person-exemption-indication
        {:conditional-field (fn [answers]
                              (-> answers
                                  :kyc-legal-entity/us-person-status
                                  (= :us-person)))}]]))

    :info         {:en (str
                        "<p><strong>U.S. Person</strong> means a U.S. citizen or resident individual, a partnership or corporation "
                        "organized in the United States or under the laws of the United States or any State thereof, a "
                        "trust if (i) a court within the United States would have authority under applicable law to "
                        "render orders or judgments concerning substantially all issues regarding administration of the "
                        "trust, and (ii) one or more U.S. persons have the authority to control all substantial decisions "
                        "of the trust, or an estate of a decedent that is a citizen or resident of the United States.  Refer "
                        "to the U.S. Internal Revenue Code for further interpretation.</p>"

                        "<br/><br/>"

                        "<p><strong>Specified U.S. Person</strong> means a U.S. Person other than:</p>"
                        "<ul>"
                        (->> ["a corporation the stock of which is regularly traded on established securities markets;"
                              "any corporation that is a member of the same expanded affiliated group;"
                              "the United States or any wholly owned agency or instrumentality thereof;"
                              "any State of the United States, any U.S. Territory, any political subdivision or wholly owned agency or instrumentality of any one or more of the foregoing;"
                              "any organization exempt from taxation under section 501 (a) of the Internal Revenue Code (the “Code”) or certain individual retirement plans defined in section 7701(a)(37) of the Code;"
                              "any bank as defined in section 581 of the Code;"
                              "any real estate investment trust as defined in section 856 of the Code;"
                              "any regulated investment company defined in section 851 of the Code or any entity registered with the U.S. Securities and Exchange Commission under the Investment Company Act of 1940;"
                              "any common trust fund as defined in section 584(a) of the Code;"
                              "any trust that is exempt from tax under section 664(c) of the Code or that is described in 4947(a)(1) of the Code;"
                              "a dealer in securities, commodities, or derivative financial instruments that is registered as such under the laws of the United States or any State;"
                              "a broker as defined in section 6045(c) of the Code; or"
                              "any tax-exempt trust under a plan that is described in section 403(b) or section 457(g) of the Code."]
                             (mapv #(str "<li>" % "</li>"))
                             (string/join ""))
                        "</ul>")}}

   :kyc.legal-entity/us-fatca-for-all-non-us-persons
   (let [not-us-person? (fn [answers _process]
                          (-> answers
                              :kyc-legal-entity/us-person-status
                              (= :not-us-person)))]
     {:ord               10.11
      :translations      {:en "US FATCA Classification for all Non-United States Entities"}
      :info              {:en (str "<p>Please complete this section if the entity is not a U.S. Person</p>"
                                   "<p><strong>FATCA Definitions</strong></p>"
                                   "<br/>"
                                   "<p>Click \"View FATCA Definitions\" below to view more detailed definitions of terms present in this section.</p>")}
      :more-info
      {:link-text   {:en "View FATCA Definitions"}
       :info-header {:en "FATCA Definitions"}
       :info-content
       {:en (str
             "<p><strong>Account Holder</strong> means the person listed or identified as the holder of a Financial Account by "
             "the Financial Institution that maintains the account. A person, other than a Financial "
             "Institution, holding a Financial Account for the benefit or account of another person as agent, "
             "custodian, nominee, signatory, investment advisor, or intermediary, is not treated as holding "
             "the account for purposes of this Agreement, and such other person is treated as holding the "
             "account.  For purposes of the immediately preceding sentence, the term “Financial "
             "Institution” does not include a Financial Institution organized or incorporated in a U.S. "
             "Territory. In the case of a Cash Value Insurance Contract or an Annuity Contract, the Account "
             "Holder is any person entitled to access the Cash Value or change the beneficiary of the "
             "contract.  If no person can access the Cash Value or change the beneficiary, the Account "
             "Holder is any person named as the owner in the contract and any person with a vested "
             "entitlement to payment under the terms of the contract.  Upon the maturity of a Cash Value "
             "Insurance Contract or an Annuity Contract, each person entitled to receive a payment under "
             "the contract is treated as an Account Holder.</p>"

             "<p><strong>Active Non-Financial Foreign Entity</strong> means any NFFE which is a Non U.S. entity that meets any of the following criteria:</p>"
             "<ul>"
             "<li>Less than 50 percent of the NFFE’s gross income for the preceding calendar year or other appropriate reporting period is passive income and less than 50 percent of the assets held by the NFFE during the preceding calendar year or other appropriate reporting period are assets that produce or are held for the production of passive income;</li>"
             "<li>The stock of the NFFE is regularly traded on an established securities market or the NFFE is a Related Entity of an Entity the stock of which is traded on an established securities market;</li>"
             "<li>The NFFE is organized in a U.S. Territory and all of the owners of the payee are bona fide residents of that U.S. Territory;</li>"
             "<li>The NFFE is a non-U.S. government, a government of a U.S. Territory, an international organization, a non-U.S. central bank of issue, or an Entity wholly owned by one or more of the foregoing;</li>"
             "<li>Substantially all of the activities of the NFFE consist of holding (in whole or in part) the outstanding stock of, and providing financing and services to, one or more subsidiaries that engage in trades or businesses other than the business of a Financial Institution, except that an NFFE shall not qualify for this status if the NFFE functions (or holds itself out) as an investment fund, such as a private equity fund, venture capital fund, leveraged buyout fund or any investment vehicle whose purpose is to acquire or fund companies and then hold interests in those companies as capital assets for investment purposes;</li>"
             "<li>The NFFE is not yet operating a business and has no prior operating history, but is investing capital into assets with the intent to operate a business other than that of a Financial Institution; provided, that the NFFE shall not qualify for this exception after the date that is 24 months after the date of the initial organization of the NFFE;</li>"
             "<li>The NFFE was not a Financial Institution in the past five years, and is in the process of liquidating its assets or is reorganizing with the intent to continue or recommence operations in a business other than that of a Financial Institution;</li>"
             "<li>The NFFE primarily engages in financing and hedging transactions with or for Related Entities that are not Financial Institutions, and does not provide financing or hedging services to any Entity that is not a Related Entity, provided that the group of any such Related Entities is primarily engaged in a business other than that of a Financial Institution; or</li>"
             "<li>The NFFE is an “excepted NFFE” as described in relevant U.S. Treasury Regulations; or</li>"
             "<li>The NFFE meets all of the following requirements:"
             "<ul>"
             "<li>It is established and maintained in its country of residence exclusively for religious, charitable, scientific, artistic, cultural, athletic or educational purposes; or it is established and operated in its jurisdiction of residence and it is a professional organization, business league, chamber of commerce, labour organization, agricultural or horticultural organization, civic league or an organization operated exclusively for the promotion of social welfare;</li>"
             "<li>It is exempt from income tax in its country of residence;</li>"
             "<li>It has no shareholders or members who have a proprietary or beneficial interest in its income or assets;</li>"
             "<li>The applicable laws of the Entity’s country of residence or the Entity’s formation documents do not permit any income or assets of the Entity to be distributed to, or applied for the benefit of, a private person or non- charitable Entity other than pursuant to the conduct of the Entity’s charitable activities, or as payment of reasonable compensation for services rendered, or as payment representing the fair market value of property which the Entity has purchased; and</li>"
             "<li>The applicable laws of the Entity’s country of residence or the Entity’s formation documents require that, upon the Entity’s liquidation or dissolution, all of its assets be distributed to a governmental entity or other non-profit organization, or escheat to the government of the Entity’s jurisdiction of residence or any political subdivision thereof.</li>"
             "</ul>"
             "</li>"
             "</ul>"
             "<br/><p></p>"

             "<p><strong>Code</strong> means the U.S Internal Revenue Code of 1986, as amended.</p>"

             "<p><strong>Controlling Person</strong> means the natural persons who exercise direct or indirect control over an entity.  In the case of a trust, such term means the settlor, the trustees, the protector (if any), the beneficiaries or class of beneficiaries, and any other natural person exercising ultimate effective control over the trust, and in the case of a legal arrangement other than a trust, such term means persons in equivalent or similar positions. The term 'Controlling Persons' shall be interpreted in a manner consistent with the Financial Action Task Force Recommendations (“FATF”).</p>"

             "<p><strong>FATF Recommendations on Controlling Persons:</strong></p>"
             "<p>Identify the beneficial owners of the customer and take reasonable measures to verify the identity of such persons, through the following information. For legal persons:</p>"

             "<ul>"

             "<li>(a) The identity of the natural persons (if any – as ownership interests can be so diversified that there are no natural persons (whether acting alone or together) exercising control of the legal person or arrangement through ownership) who ultimately have a controlling ownership interest in a legal person; and</li>"

             "<li>(b) to the extent that there is doubt under (a) as to whether the person(s) with the controlling ownership interest are the beneficial owner(s) or where no natural person exerts control through ownership interests, the identity of the natural persons (if any) exercising control of the legal person or arrangement through other means.</li>"

             "<li>(c) Where no natural person is identified under (a) or (b) above, financial institutions should identify and take reasonable measures to verify the identity of the relevant natural person who holds the position of senior managing official.</li>"

             "</ul>"
             "<p>Measures (a) to (b) are not alternative options, but are cascading measures, with each to be used where the previous measure has been applied and has not identified a beneficial owner.</p>"

             "<p><strong>Entity</strong> means a legal person or a legal arrangement such as a trust.</p>"

             "<p><strong>Exempt Beneficial Owners</strong> under the US IGA include Government entities, International Organisations, Central Bank, Broad Participation Retirement Funds, Narrow Participation Retirement Funds, Pension Funds of an Exempt Beneficial Owner, and Investment Entities wholly owned by Exempt Beneficial Owners. Please refer to the IGA for detailed definitions.</p>"

             "<p><strong>Financial Institution</strong> means a Custodial Institution, a Depository Institution, an Investment Entity, or a Specified Insurance Company, where:</p>"
             "<ul>"
             "<li><i>Custodial Institution</i> means any entity that holds, as a substantial portion of its business, financial assets for the account of others. An entity holds financial assets for the account of others as a substantial portion of its business if the entity’s gross income attributable to the holding of financial assets and related financial services equals or exceeds 20 percent of the Entity’s gross income during the shorter of: (i) the three-year period that ends on 31 December (or the final day of a non-calendar year accounting period) prior to the year in which the determination is being made; or (ii) the period during which the entity has been in existence;</li>"
             "<li><i>Depository Institution</i> means any entity that accepts deposits in the ordinary course of a banking or similar business;</li>"
             "<li><i>Investment Entity</i> means any entity that conducts as a business (or is managed by an entity that conducts as a business) one or more of the following activities or operations for or on behalf of a customer:"
             "<ul>"
             "<li>trading in money market instruments (cheques, bills, certificates of deposit, derivatives, etc.); foreign exchange; exchange, interest rate and index instruments; transferable securities; or commodity futures trading;</li>"
             "<li>individual and collective portfolio management;</li>"
             "<li>otherwise investing, administering, or managing funds or money on behalf of other persons. The term Investment entity shall be interpreted in a manner consistent with similar language set forth in the definition of “financial institution” in the Financial Action Task Force Recommendations;</li>"
             "</ul>"
             "</li>"
             "<li><i>Specified Insurance Company</i> means any entity that is an insurance company (or the holding company of an insurance company) that issues, or is obligated to make payments with respect to, a Cash Value Insurance Contract or an Annuity Contract.</li>"
             "</ul>"

             "<p></p>"

             "<p><strong>NFFE</strong> means any Non-U.S. Entity that is not a Financial Institution as defined in US FATCA.</p>"

             "<p><strong>Non-U.S. Entity</strong> means an Entity that is not a U.S. Person.</p>"

             "<p><strong>Passive Non-Financial Foreign Entity</strong> means any NFFE that is not an Active Non-Financial Foreign Entity.</p>"

             "<p><strong>Related Entity</strong> An entity is a Related Entity of another entity if either entity controls the other "
             "entity, or the two entities are under common control. For this purpose control includes direct "
             "or indirect ownership of more than 50 percent of the vote or value in an entity. "
             "Notwithstanding the foregoing, either Party may treat an entity as not a related entity if the "
             "two entities are not members of the same affiliated group, as defined in Section 1471(e)(2) of the Code.</p>")}}
      :conditional-block not-us-person?
      :questions
      (questions-registry/resolve-questions
       (concat
        [[:kyc-legal-entity/lifeline-fatca-is-financial-institution
          {:conditional-field not-us-person?
           :mandatory-field?  not-us-person?}]]
        (let [is-fi? (fn [answers process]
                       (and (not-us-person? answers process)
                            (-> answers
                                :kyc-legal-entity/lifeline-fatca-is-financial-institution
                                (= :yes))))]
          (concat
           [[:kyc-legal-entity/lifeline-does-the-entity-have-fatca-giin
             {:conditional-field is-fi?
              :mandatory-field?  is-fi?}]]

           (let [giin-status-eq (fn [giin-status]
                                  (fn [answers process]
                                    (and (is-fi? answers process)
                                         (-> answers
                                             :kyc-legal-entity/lifeline-does-the-entity-have-fatca-giin
                                             (= giin-status)))))
                 has-giin? (giin-status-eq :yes)
                 no-giin? (giin-status-eq :no)
                 giin-in-progress? (giin-status-eq :registration-in-progress)
                 has-giin-or-in-progress? (fn [answers process]
                                            (or (has-giin? answers process)
                                                (giin-in-progress? answers process)))
                 no-giin-category-eq (fn [category]
                                       (fn [answers process]
                                         (and (no-giin? answers process)
                                              (-> answers
                                                  :kyc-legal-entity/lifeline-fatca-fi-no-giin-category
                                                  (= category)))))
                 no-giin-sponsored-fi? (no-giin-category-eq :sponsored-financial-institution)
                 no-giin-trustee-documented-trust? (no-giin-category-eq :trustee-documented-trust)
                 no-giin-deemed-compliant? (no-giin-category-eq :certified-deemed-compliant-or-otherwise-non-reporting-ffi)]
             [[:kyc-legal-entity/lifeline-fatca-registered-ffi-category
               {:conditional-field has-giin-or-in-progress?
                :mandatory-field? has-giin-or-in-progress?}]

              [:kyc-legal-entity/lifeline-fatca-giin
               {:conditional-field has-giin-or-in-progress?
                :mandatory-field? has-giin?}]

              [:kyc-legal-entity/lifeline-fatca-giin-expected-issuance-date
               {:conditional-field giin-in-progress?
                :mandatory-field? giin-in-progress?}]

              [:kyc-legal-entity/lifeline-fatca-fi-no-giin-category
               {:conditional-field no-giin?
                :mandatory-field? no-giin?}]

              [:kyc-legal-entity/lifeline-fatca-no-giin-sponsored-financial-institution-type
               {:conditional-field no-giin-sponsored-fi?
                :mandatory-field? no-giin-sponsored-fi?}]

              [:kyc-legal-entity/lifeline-fatca-giin-sponsoring-entity-name
               {:conditional-field no-giin-sponsored-fi?
                :mandatory-field? no-giin-sponsored-fi?}]

              [:kyc-legal-entity/lifeline-fatca-giin-sponsoring-entity-giin
               {:conditional-field no-giin-sponsored-fi?
                :mandatory-field? no-giin-sponsored-fi?}]

              (let [condition-fn (fn [answers process]
                                   (and (no-giin-sponsored-fi? answers process)
                                        (-> answers
                                            :kyc-legal-entity/lifeline-fatca-no-giin-sponsored-financial-institution-type
                                            (= :sponsor-has-obtained-giin-on-its-behalf))))]
                [:kyc-legal-entity/lifeline-fatca-giin-sponsored-entity-giin
                 {:conditional-field condition-fn
                  :mandatory-field? condition-fn}])

              [:kyc-legal-entity/lifeline-fatca-trustee-name
               {:conditional-field no-giin-trustee-documented-trust?
                :mandatory-field? no-giin-trustee-documented-trust?}]

              [:kyc-legal-entity/lifeline-fatca-trustee-giin
               {:conditional-field no-giin-trustee-documented-trust?
                :mandatory-field? no-giin-trustee-documented-trust?}]

              [:kyc-legal-entity/lifeline-fatca-certified-deemed-compliant-or-otherwise-non-reporting-exemption
               {:conditional-field no-giin-deemed-compliant?
                :mandatory-field? no-giin-deemed-compliant?}]])))

        (let [not-fi? (fn [answers process]
                        (and (not-us-person? answers process)
                             (-> answers
                                 :kyc-legal-entity/lifeline-fatca-is-financial-institution
                                 (= :no))))
              nffi-fatca-status-eq (fn [status]
                                     (fn [answers process]
                                       (and (not-fi? answers process)
                                            (-> answers
                                                :kyc-legal-entity/lifeline-fatca-nffe-fatca-status
                                                (= status)))))
              exempt-beneficial-owner? (nffi-fatca-status-eq :exempt-beneficial-owner)
              active-nffe? (nffi-fatca-status-eq :active-nffe)
              direct-reporting-nffe? (nffi-fatca-status-eq :direct-reporting-nffe)
              sponsored-direct-reporting-nffe? (nffi-fatca-status-eq :sponsored-direct-reporting-nffe)]
          [[:kyc-legal-entity/lifeline-fatca-nffe-fatca-status
            {:conditional-field not-fi?
             :mandatory-field? not-fi?}]

           [:kyc-legal-entity/lifeline-fatca-nffe-exempt-beneficial-owner-indicate-status
            {:conditional-field exempt-beneficial-owner?
             :mandatory-field? exempt-beneficial-owner?}]

           [:kyc-legal-entity/lifeline-fatca-nffe-active-nffe-indicate-qualifying-criteria
            {:conditional-field active-nffe?
             :mandatory-field? active-nffe?}]

           [:kyc-legal-entity/lifeline-fatca-nffe-direct-reporting-nffe-giin
            {:conditional-field direct-reporting-nffe?
             :mandatory-field? direct-reporting-nffe?}]

           [:kyc-legal-entity/lifeline-fatca-nffe-sponsored-direct-reporting-nffe-sponsoring-entity-name
            {:conditional-field sponsored-direct-reporting-nffe?
             :mandatory-field? sponsored-direct-reporting-nffe?}]

           [:kyc-legal-entity/lifeline-fatca-nffe-sponsored-direct-reporting-nffe-sponsoring-entity-giin
            {:conditional-field sponsored-direct-reporting-nffe?
             :mandatory-field? sponsored-direct-reporting-nffe?}]

           [:kyc-legal-entity/lifeline-fatca-nffe-sponsored-direct-reporting-nffe-sponsored-entity-giin
            {:conditional-field sponsored-direct-reporting-nffe?
             :mandatory-field? sponsored-direct-reporting-nffe?}]])))})

   :kyc.legal-entity/controlling-persons-and-substantial-US-owners-for-fatca
   {:ord               10.111
    :translations      {:en "Controlling persons & Substantial U.S. Owners"}
    :conditional-block fatca-non-us-passive-nfe?
    :info {:en (str substantial-us-owners-info-en
                    "<p></p>"
                    controlling-persons-info-en)}
    :questions
    (questions-registry/resolve-questions
     (concat
      [[:kyc-legal-entity/list-substantial-us-owners-or-controlling-persons-info]
       [:kyc-legal-entity/substantial-us-owners-edn
        {:hide-mandatory-asterix? true
         :data/template           substantial-us-owner/schema
         :mandatory-field?        substantial-us-owners-mandatory?}]
       [:kyc-legal-entity/controlling-persons-edn
        {:hide-mandatory-asterix? true
         :data/template           controlling-person/schema
         :mandatory-field?        controlling-persons-mandatory?}]]))}

   :kyc.legal-entity/declaration-of-all-tax-residency
   {:ord          10.12
    :translations {:en "Declaration of All tax Residency"}
    :info         {:en (str "<p>Please indicate the Investor’s place of tax residence (if resident in more than one jurisdiction "
                            "please detail all jurisdictions and associated tax reference number type and number).</p>"

                            "<p>For the purposes of the Common Reporting Standard (CRS), all matters in connection with "
                            "residence are determined in accordance with the CRS and its Commentaries.</p>"

                            "<p>If an entity has no residence for tax purposes please indicate the jurisdiction in which its "
                            "place of effective management is situated. Please indicate not applicable if jurisdiction does "
                            "not issue or you are unable to procure a tax reference number or functional equivalent, and indicate the reason below.</p>")}
    :questions
    (questions-registry/resolve-questions
     (concat
      [[:kyc-legal-entity/tax-countries]]))}

   :kyc.legal-entity/crs-declaration
   {:ord          10.2
    :translations {:en "CRS Declaration"
                   :fi "CRS ilmoitus"}
    :info         {:en (str "<p><strong>CRS Definitions</strong></p>"
                            "<br/>"
                            "<p>Click \"View CRS Definitions\" below to view more detailed definitions of terms present in this section.</p>")}
    :more-info
    {:link-text    {:en "View CRS Definitions"}
     :info-header  {:en "CRS Definitions"}
     :info-content
     {:en (str "<p><strong>Active Non-Financial Entity</strong> means any NFE that meets any of the following criteria:</p>"

               "<ul>"
               "<li>(a) less than 50% of the NFE’s gross income for the preceding calendar year or other appropriate reporting period is passive income and less than 50% of the assets held by the NFE during the preceding calendar year or other appropriate reporting period are assets that produce or are held for the production of passive income;</li>"
               "<li>(b) the stock of the NFE is regularly traded on an established securities market or the NFE is a Related Entity of an Entity the stock of which is regularly traded on an established securities market;</li>"
               "<li>(c) the NFE is a Governmental Entity, an International Organisation, a Central Bank, or an Entity wholly owned by one or more of the foregoing;</li>"
               "<li>(d) substantially all of the activities of the NFE consist of holding (in whole or in part) the outstanding stock of, or providing financing and services to, one or more subsidiaries that engage in trades or businesses other than the business of a Financial Institution, except that an Entity does not qualify for this status if the Entity functions (or holds itself out) as an investment fund, such as a private equity fund, venture capital fund, leveraged buyout fund, or any investment vehicle whose purpose is to acquire or fund companies and then hold interests in those companies as capital assets for investment purposes;</li>"
               "<li>(e) the NFE is not yet operating a business and has no prior operating history, but is investing capital into assets with the intent to operate a business other than that of a Financial Institution, provided that the NFE does not qualify for this exception after the date that is 24 months after the date of the initial organisation of the NFE;</li>"
               "<li>(f) the NFE was not a Financial Institution in the past five years, and is in the process of liquidating its assets or is reorganising with the intent to continue or recommence operations in a business other than that of a Financial Institution;</li>"
               "<li>(g) the NFE primarily engages in financing and hedging transactions with, or for, Related Entities that are not Financial Institutions, and does not provide financing or hedging services to any Entity that is not a Related Entity, provided that the group of any such Related Entities is primarily engaged in a business other than that of a Financial Institution; or</li>"
               "<li>(h) the NFE meets all of the following requirements:"
               "<ul>"

               "<li>(i) it is established and operated in its jurisdiction of residence exclusively for religious, charitable, scientific, artistic, cultural, athletic, or educational purposes; or it is established and operated in its jurisdiction of residence and it is a professional organisation, business league, chamber of commerce, labour organisation, agricultural or horticultural organisation, civic league or an organisation operated exclusively for the promotion of social welfare;</li>"
               "<li>(ii) it is exempt from income tax in its jurisdiction of residence;</li>"
               "<li>(iii) it has no shareholders or members who have a proprietary or beneficial interest in its income or assets;</li>"
               "<li> (iv) the applicable laws of the NFE’s jurisdiction of residence or the NFE’s formation documents do not permit any income or assets of the NFE to be distributed to, or applied for the benefit of, a private person or non- charitable Entity other than pursuant to the conduct of the NFE’s charitable activities, or as payment of reasonable compensation for services rendered, or as payment representing the fair market value of property which the NFE has purchased; and</li>"
               "<li>(v) the applicable laws of the NFE’s jurisdiction of residence or the NFE’s formation documents require that, upon the NFE’s liquidation or dissolution, all of its assets be distributed to a Governmental Entity or other non-profit organisation, or escheat to the government of the NFE’s jurisdiction of residence or any political subdivision thereof.</li>"

               "</ul></li>"
               "</ul>"
               "<p></p>"

               "<p><strong>Controlling Person</strong> means the natural persons who exercise direct or indirect control over an entity.<br/> "
               "In the case of a trust, such term means the settlor(s), the trustees(s), the protector(s) (if any), the beneficiary(ies) or class(es) of beneficiaries, and any other natural person(s) exercising ultimate effective control over the trust, and in the case of a legal arrangement other than a trust, such term means persons in equivalent or similar positions. The term 'Controlling Persons' shall be interpreted in a manner consistent with the Financial Action Task Force Recommendations (“FATF”)."
               "</p>"

               "<p><strong>FATF Recommendations on Controlling Persons: </strong></p>"

               "<p>Identify the beneficial owners of the customer and take reasonable measures to verify the identity of such persons, through the following information.</p>"
               "<p>For legal persons:</p>"
               "<ul>"
               "<li>(a) The identity of the natural persons (if any - as ownership interests can be so diversified that there are no natural persons (whether acting alone or together) exercising control of the legal person or arrangement through ownership) who ultimately have a controlling ownership interest22 in a legal person; and </li>"
               "<li>(b) to the extent that there is doubt under (a) as to whether the person(s) with the controlling ownership interest are the beneficial owner(s) or where no natural person exerts control through ownership interests, the identity of the natural persons (if any) exercising control of the legal person or arrangement through other means.</li>"
               "<li>(c) Where no natural person is identified under (a) or (b) above, financial institutions should identify and take reasonable measures to verify the identity of the relevant natural person who holds the position of senior managing official.</li>"
               "</ul>"

               "<p></p>"
               "<p><strong>Financial Institution</strong> means a Custodial Institution, a Depository Institution, an Investment Entity, or a Specified Insurance Company, where:</p>"

               "<ul>"

               "<li><p><strong>(a) Custodial Institution</strong> means any entity that holds, as a substantial portion of its business, "
               "financial assets for the account of others. An entity holds financial assets for the account "
               "of others as a substantial portion of its business if the entity’s gross income attributable "
               "to the holding of financial assets and related financial services equals or exceeds 20 "
               "percent of the Entity’s gross income during the shorter of: (i) the three-year period that "
               "ends on 31 December (or the final day of a non-calendar year accounting period) prior to "
               "the year in which the determination is being made; or (ii) the period during which the "
               "entity has been in existence;</p></li>"

               "<li><p><strong>(b) Depository Institution</strong> means any entity that accepts deposits in the ordinary course of a banking or similar business;</p></li>"

               "<li><p><strong>(c) Investment Entity</strong> means any entity:</p>"
               "<ul>"
               "<li>(A) that primarily conducts as a business one or more of the following activities or operations for or on behalf of a customer: (i)  trading in money market instruments (cheques, bills, certificates of deposit, derivatives, etc.); foreign exchange; exchange, interest rate and index instruments; transferable securities; or commodity futures trading; (ii) individual and collective portfolio management; or (iii) otherwise investing, administering, or managing Financial Assets or money on behalf of other persons; or</li>"
               "<li>(B) 	the gross income of which is primarily attributable to investing, reinvesting, or trading in Financial Assets, if the entity is managed by another entity that is a Depository Institution, a Custodial Institution, a Specified Insurance Company, or an Investment Entity described in limb (A) of this definition.</li>"
               "</ul></li>"
               "</ul>"

               "<p>An entity is treated as primarily conducting as a business one or more of the activities "
               "described in limb (A), or an entity’s gross income is primarily attributable to investing, "
               "reinvesting, or trading in Financial Assets for purposes of limb (B) if the entity’s gross "
               "income attributable to the relevant activities equals or exceeds 50% of the entity’s gross "
               "income during the shorter of: (i) the three-year period ending on 31 December of the year "
               "preceding the year in which the determination is made; or (ii) the period during which the "
               "entity has been in existence. The term “Investment Entity” does not include an entity that is "
               "an Active Non-Financial Foreign Entity because it meets any of the criteria in subparagraphs "
               "d) through (g) of the definition of Active NFE.</p>"

               "<p>The preceding paragraph shall be interpreted in a manner consistent with similar language set "
               "forth in the definition of “financial institution” in the Financial Action Task Force Recommendations; and </p>"

               "<ul>"
               "<li><p><strong>(d) Specified Insurance Company</strong> means any entity that is an insurance company (or the "
               "holding company of an insurance company) that issues, or is obligated to make payments "
               "with respect to, a Cash Value Insurance Contract or an Annuity Contract.</p></li>"
               "</ul>"

               "<p></p>"

               "<p><strong>Non-Financial Entity</strong> or <strong>NFE</strong> means any Entity that is not a Financial Institution.</p>"
               "<p><strong>Non-Participating Jurisdiction</strong> means a jurisdiction that is not a Participating Jurisdiction.</p>"

               "<p><strong>Non-Reporting Financial Institution</strong> means any Financial Institution that is:</p>"
               "<ul>"
               "<li>(a) a Governmental Entity, International Organisation or Central Bank, other than with respect to a payment that is derived from an obligation held in connection with a commercial financial activity of a type engaged in by a Specified Insurance Company, Custodial Institution, or Depository Institution;</li>"
               "<li>(b) a Broad Participation Retirement Fund; a Narrow Participation Retirement Fund; a Pension Fund of a Governmental Entity, International Organisation or Central Bank; or a Qualified Credit Card Issuer;</li>"
               "<li>(c) any other Entity that presents a low risk of being used to evade tax, has substantially similar characteristics to any of the Entities described in subparagraphs B(1)(a) and (b), and is defined in domestic law as a Non-Reporting Financial Institution, provided that the status of such Entity as a Non-Reporting Financial Institution does not frustrate the purposes of the Common Reporting Standard;</li>"
               "<li>(d) an Exempt Collective Investment Vehicle; or</li>"
               "<li>(e) a trust to the extent that the trustee of the trust is a Reporting Financial Institution and reports all information required to be reported pursuant to Section I with respect to all Reportable Accounts of the trust.</li>"
               "</ul>"
               "<p></p>"

               "<p><strong>Participating Jurisdiction</strong> means a jurisdiction (i) with which an agreement is in place pursuant to which it will provide the information specified in Section I (of the CRS), and (ii) which is identified in a published list.</p>"

               "<p><strong>Participating Jurisdiction Financial Institution</strong> means (i) any Financial Institution that is resident in a Participating Jurisdiction, but excludes any branch of that Financial Institution that is located outside such Participating Jurisdiction, and (ii) any branch of a Financial Institution that is not resident in a Participating Jurisdiction, if that branch is located in such Participating Jurisdiction.</p>"

               "<p><strong>Passive Non-Financial Entity</strong> means any: (i) Non-Financial Entity that is not an Active Non-Financial Entity; or (ii) an Investment Entity described in limb B (or subparagraph A(6)(b) of the Standard) of the definition of Investment Entity that is not a Participating Jurisdiction Financial Institution.</p>"
               "<p><strong>Related Entity</strong> means an entity related to another entity because (i) either entity controls the other entity; (ii) the two entities are under common control; or (iii) the two entities are Investment Entities described limb B of the definition of Investment Entity, are under common management, and such management fulfils the due diligence obligations of such Investment Entities. For this purpose control includes direct or indirect ownership of more than 50 % of the vote and value in an Entity.</p>")}}
    :questions
    (questions-registry/resolve-questions
     (concat
      [[:kyc-legal-entity/lifeline-crs-classification-info]]

      [[:kyc-legal-entity/lifeline-crs-is-financial-institution
        {:mandatory-field? true}]]

      (let [is-fi? (fn [answers _process]
                     (-> answers
                         :kyc-legal-entity/lifeline-crs-is-financial-institution
                         (= :yes)))
            non-reporting-fi? (fn [answers process]
                                (and (is-fi? answers process)
                                     (-> answers
                                         :kyc-legal-entity/lifeline-crs-fi-classification
                                         (= :non-reporting-financial-institution-under-crs))))

            low-risk-under-domestic-law? (fn [answers process]
                                           (and (non-reporting-fi? answers process)
                                                (-> answers
                                                    :kyc-legal-entity/lifeline-crs-type-of-non-reporting-financial-institution
                                                    (= :other-entity-defined-under-domestic-law-as-low-risk-being-used-to-evade-tax))))]
        [[:kyc-legal-entity/lifeline-crs-fi-classification
          {:conditional-field is-fi?
           :mandatory-field? is-fi?}]

         [:kyc-legal-entity/lifeline-crs-type-of-non-reporting-financial-institution
          {:conditional-field non-reporting-fi?
           :mandatory-field? non-reporting-fi?}]

         [:kyc-legal-entity/lifeline-crs-low-risk-type-of-non-reporting-financial-institution-provided-in-domestic-law
          {:conditional-field low-risk-under-domestic-law?
           :mandatory-field? low-risk-under-domestic-law?}]])

      (let [not-fi? (fn [answers _process]
                      (-> answers
                          :kyc-legal-entity/lifeline-crs-is-financial-institution
                          (= :no)))
            active-nfe? (fn [answers process]
                          (and (not-fi? answers process)
                               (-> answers
                                   :kyc-legal-entity/lifeline-crs-nfe-classification
                                   (= :active-nfe))))
            active-nfe-type-eq (fn [active-nfe-type]
                                 (fn [answers process]
                                   (and (active-nfe? answers process)
                                        (-> answers
                                            :kyc-legal-entity/lifeline-crs-active-nfe-type
                                            (= active-nfe-type)))))
            regularly-traded-or-related? (active-nfe-type-eq :regularly-traded-corporation-or-related)
            other-active-nfe? (active-nfe-type-eq :other-active-non-financial-entity)
            itself-traded? (fn [answers process]
                             (and (regularly-traded-or-related? answers process)
                                  (-> answers
                                      :kyc-legal-entity/lifeline-crs-active-nfe-is-itself-regularly-traded
                                      (= :yes))))
            related-traded? (fn [answers process]
                              (and (regularly-traded-or-related? answers process)
                                   (-> answers
                                       :kyc-legal-entity/lifeline-crs-active-nfe-is-itself-regularly-traded
                                       (= :no))))]
        [[:kyc-legal-entity/lifeline-crs-nfe-classification
          {:conditional-field not-fi?
           :mandatory-field? not-fi?}]

         [:kyc-legal-entity/lifeline-crs-active-nfe-type
          {:conditional-field active-nfe?
           :mandatory-field? active-nfe?}]

         [:kyc-legal-entity/lifeline-crs-active-nfe-is-itself-regularly-traded
          {:conditional-field regularly-traded-or-related?
           :mandatory-field? regularly-traded-or-related?}]

         [:kyc-legal-entity/lifeline-crs-active-nfe-name-of-stock-exchange-where-traded
          {:conditional-field itself-traded?
           :mandatory-field? itself-traded?}]

         [:kyc-legal-entity/lifeline-crs-active-nfe-name-of-related-traded-corporation
          {:conditional-field related-traded?
           :mandatory-field? related-traded?}]

         [:kyc-legal-entity/lifeline-crs-other-active-nfe-qualifying-criteria
          {:conditional-field other-active-nfe?
           :mandatory-field? other-active-nfe?}]])))}

   :kyc.legal-entity/controlling-persons-for-crs
   {:ord               10.21
    :translations      {:en "Controlling persons"}
    :conditional-block (fn [answers kyc-process]
                         (or (crs-passive-nfe? answers kyc-process)
                             (crs-fi-investment-entity-in-non-partipating-jurisdiction? answers kyc-process)))
    :info {:en controlling-persons-info-en}
    :questions
    (questions-registry/resolve-questions
     (concat
      [[:kyc-legal-entity/controlling-persons-edn
        {:data/template    controlling-person/schema
         :mandatory-field? controlling-persons-mandatory?
         :info nil}]]))}

   :kyc.legal-entity/fatca-crs-confirmation
   {:ord                10.25
    :translations       {:en "Entity declaration and Undertakings"}
    :questions          []
    :info-only-section? true
    :info               {:en (str "<p>I/We declare (as an authorised signatory of the Entity) that the information provided in this form is, to the best of my/our knowledge and belief, accurate and complete. I/We undertake to advise the recipient promptly and provide an updated self-certification form within 30 days where any change in circumstances occurs, which causes any of the information contained in this form to be inaccurate or incomplete. Where legally obliged to do so, I/we hereby consent to the recipient sharing this information with the relevant tax information authorities.</p>"
                                  "<p>I/We acknowledge that, in case the Country(ies) of Tax Residence is/are CRS or FATCA (US) Reportable Jurisdiction(s), the information disclosed in this form together with required information related to my/our financial accounts will be reported to the Luxembourg tax authorities or any other authorized delegates under Luxembourg law, and subsequently exchanged with the tax authorities of the CRS Reportable Jurisdiction(s) (or to the IRS where applicable), pursuant to international agreements to exchange financial account information.</p>"
                                  "<p>I/We acknowledge that, as per Article 5 of the Luxembourg CRS Law and Article 3 of Luxembourg FATCA Law, answering questions related to the information disclosed in this self-certification form is mandatory.</p>"
                                  "<p>Upon request I/we will have access to the data concerning me/us that are disclosed to the Luxembourg tax authorities, and I/we will be in a position to rectify any of my/our personal data. The data collected will not be kept longer than necessary for the purpose of the Luxembourg CRS Law.</p>"
                                  "<p>I/we acknowledge that it is an offence to make a self-certification that is false in a material particular.</p>")}}

   :kyc.legal-entity/bank-account-details
   {:ord          10.3
    :translations {:en "Bank account details"}
    :questions
    (questions-registry/resolve-questions
     (concat
      [[:kyc-legal-entity/primary-contact-person-full-name mandatory-field-meta]
       [:kyc-legal-entity/primary-contact-person-email mandatory-field-meta]
       [:kyc-legal-entity/primary-contact-person-phone mandatory-field-meta]
       [:kyc-legal-entity/contact-for-drawdown-differs-from-primary-contact-person mandatory-field-meta]]

      (let [condition (fn [answers]
                        (= :yes (:kyc-legal-entity/contact-for-drawdown-differs-from-primary-contact-person answers)))
            conf {:mandatory-field?  condition
                  :conditional-field condition}]
        [[:kyc-legal-entity/contact-for-drawdown-notices-full-name conf]
         [:kyc-legal-entity/contact-for-drawdown-notices-email conf]
         [:kyc-legal-entity/contact-for-drawdown-notices-phone conf]])

      [[:kyc-legal-entity/bank-name mandatory-field-meta]
       [:kyc-legal-entity/bank-address mandatory-field-meta]
       [:kyc-legal-entity/bank-account-iban mandatory-field-meta]
       [:kyc-legal-entity/bank-account-swift mandatory-field-meta]
       [:kyc-legal-entity/bank-account-owner mandatory-field-meta]
       [:kyc-legal-entity/bank-account-user-right-holder mandatory-field-meta]
       [:kyc-legal-entity/bank-account-open-date mandatory-field-meta]]))}

   :kyc.legal-entity/professional-investor-status
   {:ord          10.4
    :translations {:en "Professional Investor status"}
    :questions
    (questions-registry/resolve-questions
     (concat
      [[:kyc-legal-entity/professional-investors-client-type]
       [:kyc-legal-entity/authorised-or-regulated-entity-type
        {:conditional-field (fn [answers]
                              (-> answers
                                  :kyc-legal-entity/professional-investors-client-type
                                  (= :authorised-or-regulated-entity)))}]

       [:kyc-legal-entity/authorised-and-supervised-by
        {:conditional-field (fn [answers]
                              (-> answers
                                  :kyc-legal-entity/professional-investors-client-type
                                  (= :authorised-or-regulated-entity)))}]

       [:kyc-legal-entity/any-other-institutional-investor-justification
        {:conditional-field (fn [answers]
                              (-> answers
                                  :kyc-legal-entity/professional-investors-client-type
                                  (= :any-other-institutional-investor)))}]]))}

   :kyc.legal-entity/electing-professional-client
   {:ord          10.45
    :translations {:en "Electing professional client"}
    :info         {:en (str
                        "<p><strong>Waiver of some of the protection afforded to \"non-professional clients\"</strong></p>"
                        "<p>The investor hereby declares to the General Partner and the AIF Manager that it wishes to be treated as a professional client generally and "
                        "thus waive some of the protection afforded by MiFID II.</p>"
                        "<p>It takes note of the protections and rights to compensation that it may lose; such protections and rights have been clearly explained "
                        "to it by the General Partner and/or the AIF Manager in a clear written warning.</p>"
                        "<p>It hereby declares that it is aware of the consequences of this waiver of the protection that it may lose in electing to be treated as a professional client.</p>")}
    :questions
    (questions-registry/resolve-questions
     (concat
      [[:kyc-legal-entity/portfolio-over-500k
        {:optional-field? true}]
       [:kyc-legal-entity/financial-institution-experience
        {:optional-field? true}]]

      ;; Financial institution experience description
      (let [condition-fn (fn [answers]
                           (= :yes (:kyc-legal-entity/financial-institution-experience answers)))
            config {:conditional-field condition-fn
                    :optional-field? true}]
        [[:kyc-legal-entity/financial-institution-experience-description config]])

      ;; Financial services employment
      [[:kyc-legal-entity/financial-services-employment
        {:optional-field? true}]]

      ;; Financial services employment description
      (let [condition-fn (fn [answers]
                           (= :yes (:kyc-legal-entity/financial-services-employment answers)))
            config {:conditional-field condition-fn
                    :optional-field? true}]
        [[:kyc-legal-entity/financial-services-employment-description config]])

      ;; Frequent investment activity
      [[:kyc-legal-entity/frequent-investment-activity {:optional-field? true}]]

      ;; Portfolio statement (conditional)
      (let [condition-fn (fn [answers]
                           (= :yes (:kyc-legal-entity/frequent-investment-activity answers)))
            config {:conditional-field condition-fn
                    :optional-field? true}]
        [[:kyc-legal-entity/portfolio-statement config]])

      ;; Collective investment experience
      [[:kyc-legal-entity/collective-investment-experience {:optional-field? true}]
       [:kyc-legal-entity/collective-investment-experience-description
        {:conditional-field (fn [answers]
                              (= :yes (:kyc-legal-entity/collective-investment-experience answers)))
         :optional-field? true}]]))}

   :kyc.legal-entity/special-investment-conditions-for-us-investors
   (let [us-person? (fn [answers]
                      (let [us-person-status (:kyc-legal-entity/us-person-status answers)]
                        (and us-person-status
                             (some #{us-person-status} [:us-person :specified-us-person]))))
         notes-anchor-tag-id (fn [note-number]
                               (str "special-investment-conditions-for-us-investor-notes-" note-number))
         notes-sup-link (fn [note-number]
                          (str "<sup class=\"align-bottom text-xs\">"
                               "<a class=\"pr-px text-base-light-text underline decoration-dotted hover:no-underline\" href=\"#"
                               (notes-anchor-tag-id note-number)
                               "\">"
                               note-number
                               "</a></sup>"))]
     {:ord          10.5
      :translations {:en "Special investment conditions for U.S. investors"}
      :conditional-block us-person?
      :info {:en (str "<p><strong>Computation of “Investments”</strong></p>"

                      "<p>The following is designed to assist it in determining which of its assets are “investments” for the purposes of the definition of “qualified purchaser” and how to value those assets appropriately. Although such “investments” include most of what are ordinarily considered “investments” or “securities” (but excludes assets such as jewelry, artwork, antiques and other similar collectibles), issues may arise as to whether a particular holding falls within the definition. The following is intended only as a summary and is subject to and qualified by the applicable definitions under the Investment Company Act. The following is not legal advice and it is encouraged to consult its own legal and/or tax advisors for guidance on these issues.</p>"

                      "<p><strong>Types of Investments</strong>. The term “investment”"
                      (notes-sup-link 1)
                      " includes the investments described below. See the accompanying footnotes for more complete definitions.</p>"

                      "<ol type=\"a\" class=\"list-[lower-alpha]\">"

                      "<li><p>Cash and cash equivalents (including foreign currency) held for investment purposes, including (i) bank deposits, certificates of deposit, bankers' acceptances, and (ii) the net cash surrender value of an insurance policy.</p></li>"

                      "<li><p>Securities such as:</p>"

                      "<ol class=\"list-[lower-roman]\">"

                      (->> ["shares of (and other interests in) mutual funds, closed-end funds, hedge funds, and commodity pools;"
                            "securities, including common stock, preferred stock and other equity instruments as well as bonds, notes, debentures and other debt obligations, of any public company (including companies listed on certain foreign exchanges);"
                            "securities, including common stock, preferred stock and other equity instruments as well as bonds, notes, debentures and other debt obligations, of any private company with at least $50 million in shareholders’ equity;"
                            (str "securities, including common stock, preferred stock and other equity instruments as well as bonds, notes, debentures and other debt obligations, of any private company with less than $50 million in shareholders’ equity provided it does not control or exercise control, alone or with others, over the private company"
                                 (notes-sup-link 2)
                                 ";")
                            "interests in family-owned or closely-held businesses controlled by it if they fall in one of categories (i)-(iii) above; and"
                            "bonds, notes and similar debt obligations issued by federal, state and local governments and agencies."]
                           (mapv (fn [list-item]
                                   (str "<li><p>" list-item "</p></li>")))
                           (string/join "\n"))

                      "</ol></li>"

                      (->> ["Real estate held for investment purposes (which does not include a place of business used by it or it’s family, or a personal residence used by it or it’s family unless the residence is treated as an investment for tax purposes)."
                            "Commodity futures contracts, options on commodity futures contracts, and options on physical commodities traded on or subject to the rules of a major commodities exchange held for investment purposes"
                            (str "Physical commodities such as gold or silver with respect to which a commodity interest is traded on or subject to the rules of a major commodities exchange"
                                 (notes-sup-link 3)
                                 " held for investment purposes.")
                            (str "Financial contracts"
                                 (notes-sup-link 4)
                                 " entered into for investment purposes, e.g., swaps and similar contracts.")

                            (str "With respect to a commodity pool or other privately offered pooled investment vehicle, the unfunded capital commitments of its investors."
                                 (notes-sup-link 5))]
                           (mapv (fn [list-item]
                                   (str "<li><p>" list-item "</p></li>")))
                           (string/join "\n"))

                      "</ol>"
                      "<p></p>"

                      "<p><strong>Valuation</strong>. An “investment” should be valued at its fair market value as of the most recent practicable date or its cost, provided that (i) commodity interests should be valued at the initial margin or option premium deposited in connection with such interests and (ii) the amount of investments owned by it should be reduced by the following amounts, as applicable: (a) the amount of any outstanding indebtedness incurred to acquire or for the purpose of acquiring the investments owned by it (including, in the case of any joint “investments“, any outstanding indebtedness incurred by the spouse to acquire or for the purpose of acquiring the investments) and (b) in addition to the amount specified in article (a) of this sentence with respect to a Family Company, the amount of outstanding indebtedness incurred by an owner of the Family Company to acquire or for the purpose of acquiring such investments.</p>"

                      "<p><strong>Retirement Plans and Trusts</strong>. It may include as “investments” any otherwise qualifying investments held in an individual retirement account or similar account in which those investments are held for the benefit of and directed by it.</p>"

                      "<p><strong>Joint Investments</strong>. It may include as “investments” any otherwise qualifying investments held jointly with its spouse, or in which it and its spouse share a community property or similar shared ownership interest. In determining whether spouses who are making a joint investment are qualified purchasers, there may be included in the amount of each spouse’s “investments” any otherwise qualifying investments owned by the other spouse (whether or not such investments are held jointly). In each case, the amount of any such investments should be reduced by any outstanding debt incurred by either spouse in purchasing them.</p>"

                      "<p><strong>Investments by Subsidiaries</strong>. The amount of “investments” owned by an entity other than a Family Company may include investments owned by majority-owned subsidiaries of the entity and investments owned by an entity (“<strong>Parent Entity</strong>”) of which the entity is a majority-owned subsidiary, or by a majority-owned subsidiary of the entity and other majority-owned subsidiaries of the Parent Entity.</p>"

                      "<p></p>"
                      "<p><strong>Notes</strong></p>"

                      "<ol class=\"list-decimal\">"

                      (->> ["For purposes of determining whether it is a qualified purchaser, the aggregate amount of investments owned and invested on a discretionary basis by the Investor will be the investments’ fair market value on the most recent practicable date, or their cost; provided that: (i) in the case of commodity investments set forth in article (viii) below, the amount of investments will be the value of the initial margin or option premium deposited in connection with such commodity interests; and (ii) in each case, deduct from the amount of investments owned by it the following amounts, as applicable: (a) the amount of any outstanding indebtedness incurred to acquire or for the purpose of acquiring the investments owned by the Investor (including, in the case of any joint investments, any outstanding indebtedness incurred by the spouse to acquire or for the purpose of acquiring the investments) and (b) in addition to the amount specified in article (a) of this sentence with respect to a Family Company, the amount of outstanding indebtedness incurred by an owner of the Family Company to acquire or for the purpose of acquiring such investments."
                            "A director or executive officer of a company or the holder of more than 10% of a company's voting stock will generally be deemed to control the company."
                            "“Major commodities exchange” includes any contract market designated for trading such transactions under the Commodity Exchange Act and the rules thereunder or any board of trade or exchange outside the United States, as contemplated by the Commodity Exchange Act."
                            "As defined in Section 3(c)(2)(B)(ii) of the Investment Company Act."
                            "If it is either (i) a company excluded from the definition of an investment company under Section 3(c)(7) of the Investment Company Act, (ii) a company that would be an investment company but for the exclusion provided by Section 3(c)(1) of the Investment Company Act, (iii) an employees’ securities company as defined in Section 2(a)(13) of the Investment Company Act, or (iv) a commodity pool, the Investor may treat as qualified “investments” any amounts payable to the Investor pursuant to a binding commitment in which a person has agreed to acquire an interest in, or make capital contributions to it upon the demand of it."]
                           (map-indexed
                            (fn [index list-item]
                              (str "<li id=\"" (notes-anchor-tag-id (inc index)) "\"><p>"
                                   list-item
                                   "</p></li>")))
                           (string/join "\n"))

                      "</ol>"

                      "<div class=\"mb-10\"></div>"

                      "<p><strong>US Regulatory Disclosures</strong></p>"

                      "<p><strong>Investment Company Act of 1940</strong></p>"
                      "<p>The Partnership will be exempt from the provisions of the U.S. Investment Company Act of 1940, as amended, and the rules and regulations promulgated thereunder (the “<strong>Investment Company Act</strong>”) pursuant to an exemption from registration. No potential investor will be admitted to the Partnership if an investment by such potential investor in the Partnership would require any Partnership vehicle to register with the SEC as an investment company under the Investment Company Act.</p>"

                      "<p><strong>Investment Advisers Act of 1940</strong></p>"
                      "<p>The General Partner is not currently registered as an investment adviser under the U.S. Investment Advisers Act of 1940, as amended (the “<strong>Investment Advisers Act</strong>”) (and is not required to be so registered) and does not intend to register as an investment adviser, but may register as an investment adviser to the extent such registration is required in the future, or may rely on an exemption from registration.</p>"

                      "<p><strong>Private Placement Status</strong></p>"
                      "<p>The limited partner interests described herein are not registered under the U.S. Securities Act of 1933, as amended, and the rules and regulations promulgated thereunder (the “<strong>Securities Act</strong>”), or U.S. state or non-U.S. securities laws in reliance upon the exemptions for transactions not involving a public offering. As a purchaser of limited partner interests in a private placement not registered under the Securities Act, each U.S. Person investor will be required to make certain representations to the Partnership, including that the investor is acquiring such interests for investment, and not with a view to resale or distribution, and that it is an accredited investor, as defined in Regulation D under the Securities Act. Further, each investor must be prepared to bear the economic risk of an investment in the Partnership for an indefinite period, since limited partner interests in the Partnership cannot be sold unless they are subsequently registered under the Securities Act or an exemption from such registration is available. It is extremely unlikely that limited partner interests in the Partnership will ever be registered under the Securities Act.</p>")}
      :questions
      (questions-registry/resolve-questions
       (concat
        ;; E1 - Entity with assets > US$5M (combo checkbox with children)
        [[:kyc-legal-entity/entity-with-assets-excess-of-5m-purpose-not-investing-in-the-partnership-combo
          {:optional-field? true}]]

        ;; E2 - Investment adviser (combo checkbox with children)
        [[:kyc-legal-entity/legal-entity-is-an-investment-adviser-combo
          {:optional-field? true}]]

        ;; E3 - Personal trust > US$5M (standalone)
        [[:kyc-legal-entity/personal-trust-5m
          {:optional-field? true}]]

        ;; E4 - Employee benefit plan (combo checkbox with children)
        [[:kyc-legal-entity/employee-benefit-plan-within-meaning-of-title-1-of-erisa-combo
          {:optional-field? true}]]

        ;; E5-E11 - Standalone accredited investor questions
        [[:kyc-legal-entity/self-directed-plan {:optional-field? true}]
         [:kyc-legal-entity/state-employee-benefit-plan {:optional-field? true}]
         [:kyc-legal-entity/licensed-supervised-entity {:optional-field? true}]
         [:kyc-legal-entity/sec-registered-entity {:optional-field? true}]
         [:kyc-legal-entity/other-entity-5m {:optional-field? true}]
         [:kyc-legal-entity/family-office {:optional-field? true}]
         [:kyc-legal-entity/family-client {:optional-field? true}]
         [:kyc-legal-entity/entity-accredited-owners {:optional-field? true}]

         ;; E12-E20 - Investment Company Act questions
         [:kyc-legal-entity/is-investment-company-registered {:optional-field? true}]
         [:kyc-legal-entity/would-be-investment-company {:optional-field? true}]]

        ;; E14 - Conditional on E12 OR E13
        (let [condition-fn (fn [answers]
                             (or (= :true (:kyc-legal-entity/is-investment-company-registered answers))
                                 (= :true (:kyc-legal-entity/would-be-investment-company answers))))]
          [[:kyc-legal-entity/commitment-less-than-10-percent {:conditional-field condition-fn
                                                               :optional-field? true}]])

        [[:kyc-legal-entity/not-formed-for-partnership-investment {:optional-field? true}]
         [:kyc-legal-entity/commitment-less-than-40-percent-assets {:optional-field? true}]
         [:kyc-legal-entity/has-made-other-investments {:optional-field? true}]
         [:kyc-legal-entity/governing-docs-require-proportional-participation {:optional-field? true}]
         [:kyc-legal-entity/managed-as-collective-vehicle {:optional-field? true}]]

        ;; E20 - Conditional on any E15-E19 being false
        (let [condition-fn (fn [answers]
                             (some #{:false}
                                   [(:kyc-legal-entity/commitment-less-than-40-percent-assets answers)
                                    (:kyc-legal-entity/not-formed-for-partnership-investment answers)
                                    (:kyc-legal-entity/has-made-other-investments answers)
                                    (:kyc-legal-entity/governing-docs-require-proportional-participation answers)
                                    (:kyc-legal-entity/managed-as-collective-vehicle answers)]))]
          [[:kyc-legal-entity/number-of-beneficial-owners {:conditional-field condition-fn
                                                           :optional-field? true}]])

        [[:kyc-legal-entity/qualified-purchaser-qualifications {:optional-field? true}]
         [:kyc-legal-entity/qualified-purchaser-pre-1996-company {:optional-field? true}]
         [:kyc-legal-entity/subject-to-bank-holding-company-act {:optional-field? true}]]))})


   :kyc-legal-entity/identifications
   {:ord
    11.0

    :translations
    {:en "Signer identification"
     :fi "Allekirjoittajien tunnistetiedot"}

    :keep-together?
    true

    :info
    {:en (str "<p>True copies can be certified by a third person.</p>"
              "<p>The certification should contain certifier’s name, "
              "occupation or education, phone number, email address and date.</p>"

              "<p>If, at any time during the term of the Partnership, any of the representations, "
              "warranties, confirmations, agreements or covenants contained in this form ceases to be true, "
              "it shall promptly notify the General Partner that this is the case.</p>")
     :fi (str "<p>Asiakirjat voi oikeaksi todistaa kuka tahansa kolmas henkilö.</p>"
              "<p>Todistuksen tulee sisältää oikeaksi todistajan nimi, ammatti tai koulutus, "
              "puhelinnumero, sähköpostiosoite ja päivämäärä.</p>")}

    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/number-of-signers
       {:mandatory-field? true}]
      [:kyc-legal-entity/signer-name-1
       {:mandatory-field? true}]
      [:kyc-legal-entity/signer-email-1
       {:mandatory-field? true}]
      [:kyc-legal-entity/type-of-identification-attachment-1
       {:mandatory-field? true}]
      [:kyc-legal-entity/identification-attachment-1
       {:mandatory-field? true}]
      [:kyc-legal-entity/signer-name-2
       {:mandatory-field? (fn [answers]
                            (-> answers
                                :kyc-legal-entity/number-of-signers
                                (= :two)))}]
      [:kyc-legal-entity/signer-email-2
       {:mandatory-field? (fn [answers]
                            (-> answers
                                :kyc-legal-entity/number-of-signers
                                (= :two)))}]
      [:kyc-legal-entity/type-of-identification-attachment-2
       {:mandatory-field? (fn [answers]
                            (-> answers
                                :kyc-legal-entity/number-of-signers
                                (= :two)))}]
      [:kyc-legal-entity/identification-attachment-2
       {:mandatory-field? (fn [answers]
                            (-> answers
                                :kyc-legal-entity/number-of-signers
                                (= :two)))}]])}

   #_:kyc-legal-entity/signature-info
   #_{:ord
      12.0
      :translations
      {:en ""
       :fi ""}

      :end-of-pdf?
      true

      :page-break-before?
      true

      :conditional-block
      (fn [_answers kyc-process]
        (some #{(:kyc-process/status kyc-process)}
              [:kyc-process.status/signatures
               :kyc-process.status/waiting-for-e-sign]))

      :questions
      (questions-registry/resolve-questions
       [[:kyc-legal-entity/signature-info-1
         {:translations
          {:en ""
           :fi ""}}]
        [:kyc-legal-entity/signature-info-2
         {:translations
          {:en ""
           :fi ""}}]])}

   :kyc-legal-entity/signature
   {:ord
    13.0

    :translations
    {:en "Signature"
     :fi "Allekirjoitus"}

    :end-of-pdf?
    true

    :conditional-block
    (fn [answers kyc-process]
      (and (some #{(:kyc-process/status kyc-process)}
                 [:kyc-process.status/signatures
                  :kyc-process.status/waiting-for-e-sign])
           (some #{(:kyc-legal-entity/number-of-signers answers)} [:one :two])))

    :section-not-read-only
    (fn [_answers kyc-process]
      (some #{(:kyc-process/status kyc-process)}
            [:kyc-process.status/signatures
             :kyc-process.status/waiting-for-e-sign]))

    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/signature-place]
      [:kyc-legal-entity/signature-date]
      [:kyc-legal-entity/signature-name]
      [:kyc-legal-entity/signature-signature]])}

   :kyc-legal-entity/signature-2
   {:ord
    14.0

    :translations
    {:en "Signature 2"
     :fi "Allekirjoitus 2"}

    :end-of-pdf?
    true

    :conditional-block
    (fn [answers kyc-process]
      (and (some #{(:kyc-process/status kyc-process)}
                 [:kyc-process.status/signatures
                  :kyc-process.status/waiting-for-e-sign])
           (some #{(:kyc-legal-entity/number-of-signers answers)} [:two])))

    :section-not-read-only
    (fn [_answers kyc-process]
      (some #{(:kyc-process/status kyc-process)}
            [:kyc-process.status/signatures
             :kyc-process.status/waiting-for-e-sign]))

    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/signature-place-2]
      [:kyc-legal-entity/signature-date-2]
      [:kyc-legal-entity/signature-name-2]
      [:kyc-legal-entity/signature-signature-2]])}})
